import { useReducer } from "react";
import { Bounds } from "viewport-mercator-project";
import { SEARCH_RESULT_TYPE } from "common/constants";
import { parseCoordinates } from "common/utils/coordinates";
import {
  getSearchErrorFromMessage,
  SEARCH_ERROR,
} from "common-client/utils/search";
import { OmniboxSearchResult } from "../../../generated/graphql";

export { getSearchErrorFromMessage, SEARCH_ERROR };

export type SearchResultsState = {
  debouncedQuery: string | null;
  isTextSearch: boolean | null;
  loading: boolean;
  results: OmniboxSearchResult[] | null;
  error: SEARCH_ERROR | null;
  bounds: Bounds | null;
  hoveredSearchResult: {
    feature: {
      id: string;
      featureType: SEARCH_RESULT_TYPE;
    };
    hoverSource: "results" | "map";
  } | null;
};

export type SearchResultsAction =
  | {
      type: "searchStarted";
      query: string;
    }
  | {
      type: "searchComplete";
      results: OmniboxSearchResult[] | null;
      bounds: Bounds | null;
    }
  | {
      type: "searchError";
      error: SEARCH_ERROR;
    }
  | {
      type: "resetSearch";
    }
  | {
      type: "setHoveredSearchResult";
      feature: {
        id: string;
        featureType: SEARCH_RESULT_TYPE;
      };
      hoverSource: "results" | "map";
    }
  | {
      type: "clearHoveredSearchResult";
    };

export const INITIAL_SEARCH_RESULTS_STATE: SearchResultsState = {
  debouncedQuery: null,
  isTextSearch: null,
  loading: false,
  results: null,
  error: null,
  hoveredSearchResult: null,
  bounds: null,
};

export const useSearchResultsState = (
  initialState: SearchResultsState = INITIAL_SEARCH_RESULTS_STATE,
) =>
  useReducer((state: SearchResultsState, action: SearchResultsAction) => {
    switch (action.type) {
      case "searchStarted":
        return {
          ...state,
          loading: true,
          isTextSearch: parseCoordinates(action.query) === null,
          debouncedQuery: action.query,
          results: null,
          error: null,
        };
      case "searchComplete":
        return {
          ...state,
          results: action.results,
          bounds: action.bounds,
          loading: false,
        };
      case "searchError":
        return { ...state, error: action.error, loading: false };
      case "resetSearch":
        return INITIAL_SEARCH_RESULTS_STATE;
      case "setHoveredSearchResult":
        return {
          ...state,
          hoveredSearchResult: {
            feature: action.feature,
            hoverSource: action.hoverSource,
          },
        };
      case "clearHoveredSearchResult":
        return {
          ...state,
          hoveredSearchResult: null,
        };
    }
  }, initialState);
